// The routing guards: who may pass, decided by audience or by capability. Pure
// decision logic so it can be unit-tested without a router; the route
// `beforeLoad` hooks delegate to these.

import { redirect } from "@tanstack/react-router";

import type { Audience } from "@/lib/identity";
import { canEnterAdminSection } from "@/lib/admin-tabs";
import { featureFlag } from "@/lib/feature-flags";
import { derivePermissions } from "@/lib/roles";
// The landing paths, one per audience — the single home of where a user who
// has just arrived belongs. Post-login forwarding already goes through `/`
// (completeLogin defaults returnTo to it) and `/` is a pure dispatcher onto
// these, so no second place decides a landing destination.
//
// Both are on the route tree — the communities list since #750 — which is what
// keeps every `redirect({ to })` below typed. No explicit type annotations: the
// inferred literals are what the route tree checks against, and an explicit
// `: string` would break them.
const INTERNAL_LANDING = "/communities";
// External users have no communities landing; the session page is theirs.
export const EXTERNAL_LANDING = "/session";

/**
 * What a route guard needs to know about the visitor.
 *
 * Restated here rather than imported as `CurrentUser` (`@/api/functions/auth`)
 * on purpose: `lib/` is the tier *below* both graphs, so it may not reach up
 * into `api/` for a type (`client/docs/architecture.md`, "The dependency
 * rule"). Named once so the capability guards below don't each re-spell it.
 */
export type GuardSubject =
  { user: { audience: Audience }; session: { roles: readonly string[] } } | null | undefined;

/**
 * Sends a turned-away visitor to their own landing page. Never returns.
 *
 * Always replaces the history entry: someone who typed `/admin` or `/items`
 * must not leave it behind, or Back from the landing re-enters the guard and
 * bounces them forward again.
 */
function bounceToLanding(currentUser: GuardSubject): never {
  // Both landings are on the tree, so each travels as a compiler-checked `to`
  // — the spelling `requireInternalAudience` uses for the external one. An
  // `href` would cost a document load for nothing.
  throw redirect({
    to: currentUser?.user.audience === "internal" ? INTERNAL_LANDING : EXTERNAL_LANDING,
    replace: true,
  });
}

/** Post-login landing path for the given audience. */
export function landingPathFor(audience: Audience) {
  return audience === "internal" ? INTERNAL_LANDING : EXTERNAL_LANDING;
}

/**
 * Guard for internal-only routes. Throws a router redirect to the external
 * landing unless the user is internal. Fails closed: a null/undefined user or
 * any non-internal audience is redirected out.
 */
export function requireInternalAudience(user: { audience: Audience } | null | undefined): void {
  if (user?.audience === "internal") return;
  throw redirect({ to: EXTERNAL_LANDING });
}

/**
 * Guard for the Admin section. Throws a router redirect to the visitor's own
 * landing page unless they may enter it.
 *
 * The repo's first *role*-based guard, beside the audience one above, and it
 * gates a door rather than data: Python use cases remain the enforcement
 * (#67 §4), and the endpoints behind this section carry their own role
 * dependency. What this decides is only whether the URL renders.
 *
 * Admission is `canEnterAdminSection` — the flag and the capability together,
 * the pair the old stack asked for in one condition (owner directive,
 * 2026-08-25: donor parity). Reading the flag here is what makes it a real kill
 * switch: gated only in the header, it would hide the entrance while leaving
 * the section reachable by typed URL. The read goes through the isomorphic
 * facade, never a fresh source (CLAUDE.md rule 8), and is synchronous — no
 * loading branch to get wrong in a `beforeLoad`.
 *
 * It asks the capability, not the role name — an admin is whoever
 * `derivePermissions` says can administrate — so a future role that grants it
 * needs no edit here.
 *
 * Fails closed the way `requireInternalAudience` does, and for the same reason:
 * a null/undefined session cannot reach this route (the `_authed` gate has
 * already sent an unauthenticated visitor to login), so the only safe reading
 * of one is "not an admin". With no audience to read it lands on the external
 * landing — the one page every signed-in identity can see.
 */
export function requireAdminSectionAccess(currentUser: GuardSubject): void {
  const admitted =
    currentUser !== null &&
    currentUser !== undefined &&
    canEnterAdminSection({
      adminTabEnabled: featureFlag("enable_admin_tab"),
      canAdministrate: derivePermissions(currentUser.session.roles).canAdministrate,
    });
  if (admitted) return;
  bounceToLanding(currentUser);
}

/**
 * Guard for the Item Catalog section. Throws a router redirect to the visitor's
 * own landing page unless they may browse the catalog.
 *
 * A door, not the enforcement — the same posture as `requireAdminSectionAccess`
 * above: Python's `require_any_role(CATALOG_READ_ROLES)` and the use cases
 * behind it decide who gets rows, and the server function carries its own
 * capability check (a server function is callable without ever rendering this
 * route). What this decides is only whether the URL renders, so a trade partner
 * who typed `/items` gets their landing page rather than a page of failed
 * requests.
 *
 * No feature flag: the Admin section's gate reads one because the old stack's
 * did, and the catalog has no such switch in the effective-prod manifest
 * (spec #238, "No new feature flags").
 *
 * Fails closed, and asks the capability rather than a role name, for the
 * reasons written out on `requireAdminSectionAccess`.
 */
export function requireCatalogAccess(currentUser: GuardSubject): void {
  if (currentUser && derivePermissions(currentUser.session.roles).canBrowseCatalog) return;
  bounceToLanding(currentUser);
}

/**
 * Guard for the Specifications section's pages — the Spec list, the spec
 * layout route and the two legacy resolvers. Throws a router redirect to the
 * visitor's own landing page unless they may browse specifications.
 *
 * The catalog guard's twin, on `canBrowseSpecs`: a door, not the enforcement —
 * Python's `require_any_role(SPEC_READ_ROLES)` and the use cases behind it decide
 * who gets rows, and the spec server functions check the same capability
 * themselves. It exists so a trade partner or a migrator who typed
 * `/specifications/base` lands on their own page rather than on a page of
 * failed reads.
 */
export function requireSpecsAccess(currentUser: GuardSubject): void {
  if (currentUser && derivePermissions(currentUser.session.roles).canBrowseSpecs) return;
  bounceToLanding(currentUser);
}

/**
 * Whether an item read has come back and said the item is not there.
 *
 * A named predicate, and shared, because the distinction it draws is the whole
 * of the branch it sits in and the two states are one character apart in the
 * query object: a read that *succeeded* on absence is a stale link and gets the
 * 404, while a read that *failed* is an error and keeps the donor's snackbar
 * over a page that still draws.
 *
 * It lives here rather than on either route because **both** item routes need
 * it — the details page and the edit page answer a deleted id the same way —
 * and a route importing another route's export to get it would tie two URLs
 * together for the sake of two lines.
 */
export function isStaleItemLink(query: { isSuccess: boolean; data: unknown }): boolean {
  return query.isSuccess && (query.data ?? null) === null;
}

/**
 * Which branch of the edit route's guard a visitor takes —
 * `SpecificationEditRoute` (`specification-edit-route.tsx`), in **the source's
 * order**, because the order is observable: a read-only user on a published
 * version is turned back as a reader, not as a published version.
 *
 *   1. no summary, or no such version on the spec → `missing-version`;
 *   2. the server says this reader may not edit it → `no-permission` (`mayEdit`
 *      is the server's `canEditSpec`, ADR-0011, never re-derived here);
 *   3. the version is published → `published`;
 *   4. the version is archived → `archived`;
 *   5. otherwise → `editor`, the branch the Editor (#548) takes over.
 *
 * The source's loading branch has no counterpart: the route's loader awaits the
 * read before it decides.
 */
export type EditGuardBranch =
  "missing-version" | "no-permission" | "published" | "archived" | "editor";

export function editGuardBranch(
  summary: {
    mayEdit: boolean;
    versions: readonly { id: number; isPublished: boolean; archived: boolean }[];
  } | null,
  versionId: number,
): EditGuardBranch {
  const version = summary?.versions.find((candidate) => candidate.id === versionId);
  if (!summary || !version) return "missing-version";
  if (!summary.mayEdit) return "no-permission";
  if (version.isPublished) return "published";
  if (version.archived) return "archived";
  return "editor";
}
